import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import InputAmount from '../../../component/input/InputAmount';
import Calendar from '../../../component/input/Calendar';
import MultiSelect from '../../../component/input/MultiSelect';
import { DeviceService } from '../../../service/DeviceService';
import { PlanogramService } from '../../../service/PlanogramService';
import { useTranslation } from 'react-i18next';
import useValidator from '../../../utils/validator/useValidator';
import { getNextBusinessDay } from '../../../utils/date/DateUtils';
import './ChangePriceDialog.scss';
// removed StatusMessage in favor of simple orange text
import { ReactComponent as WarningTriangleIcon } from '../../../assets/images/warning-triangle-icon.svg';

const ChangePriceDialog = ({ visible, onHide, onSave, selectedCellsCount, defaultCurrency, defaultPrice, defaultEffectiveDate, planogramName, planogramId, remotePriceUpdate = false }) => {
    const { t } = useTranslation();
    const validator = useValidator();
    const [validationAttempted, setValidationAttempted] = useState(false);
    const [step, setStep] = useState(1); // 1: price, 2: devices, 3: warning (non-remote present), 4: date
    const [effectiveDate, setEffectiveDate] = useState(null);
    const [selectedDevices, setSelectedDevices] = useState([]);
    const [formData, setFormData] = useState({
        price: null,
        currency: defaultCurrency
    });

    const [availableDevices, setAvailableDevices] = useState([]);
    const [isLoadingDevices, setIsLoadingDevices] = useState(false);
    const [postWarningAction, setPostWarningAction] = useState(null); // 'next' | 'date'
    const [isNotGenerateTask, setIsNotGenerateTask] = useState(false);

    // Fetch available devices for step 2
    useEffect(() => {
        let cancelled = false;
        const fetchDevices = async () => {
            if (!visible || step !== 2 || !planogramId) return;
            setIsLoadingDevices(true);
            try {
                const response = await DeviceService.getSimpleListDeviceAtPlanogram(planogramId);
                const devices = (response?.data || []).map(d => ({
                    id: d?.id || d?.uuid || d?.externalId,
                    name: d?.name || d?.deviceName || d?.identifier || String(d?.id || d?.uuid || ''),
                    remotePriceUpdate: d?.remotePriceUpdate
                })).filter(d => d.id);
                if (!cancelled) {
                    setAvailableDevices(devices);
                }
            } catch (e) {
                if (!cancelled) setAvailableDevices([]);
            } finally {
                if (!cancelled) setIsLoadingDevices(false);
            }
        };
        fetchDevices();
        return () => { cancelled = true; };
    }, [visible, step, planogramId]);

    // Auto-select all devices when entering step 2 and list is loaded
    useEffect(() => {
        if (!visible || step !== 2) return;
        if (availableDevices.length > 0 && selectedDevices.length === 0) {
            setSelectedDevices(availableDevices);
        }
    }, [visible, step, availableDevices]);

    // Reset step to 1 when dialog becomes visible
    useEffect(() => {
        if (visible) {
            setStep(1);
            setValidationAttempted(false);
            setEffectiveDate(defaultEffectiveDate);
            setSelectedDevices([]);
            setFormData({ price: defaultPrice, currency: defaultCurrency });
            setPostWarningAction(null);
        }
    }, [visible, defaultCurrency, defaultPrice, defaultEffectiveDate]);

    const nonRemoteDevices = selectedDevices.filter(d => d?.remotePriceUpdate === false);

    const handleNonRemoteChange = (e) => {
        const newValue = e?.value || [];
        const newIds = new Set(newValue.map(d => d.id));
        setSelectedDevices(prev => prev.filter(d => d?.remotePriceUpdate !== false || newIds.has(d.id)));
    };

    const handleSave = () => {
        if (step === 1) {
            setValidationAttempted(true);

            // Validate price field
            if (!validator.allValid()) {
                validator.showMessages();
                return;
            }

            if (isNotGenerateTask) {
                const payload = {
                    ...formData,
                    effectiveDate: null
                };
                onSave(payload);
                onHide();
            } else {
                // Proceed to step 2 (date selection)
                setStep(2);
            }
            return;
        }

        // Helper function to normalize date (set time to 00:00:00 if remotePriceUpdate is false)
        const normalizeDate = (date) => {
            if (!date || !(date instanceof Date)) return date;
            if (!remotePriceUpdate) {
                return new Date(
                    date.getFullYear(),
                    date.getMonth(),
                    date.getDate(),
                    0, // hours = 0
                    0, // minutes = 0
                    0, // seconds = 0
                    0  // milliseconds = 0
                );
            }
            return date;
        };

        // Determine the effective date based on current step
        let finalEffectiveDate;

        if (step === 2) {
            // Next Business Day selected
            const nextBusinessDay = getNextBusinessDay(new Date());
            setEffectiveDate(nextBusinessDay);
            finalEffectiveDate = normalizeDate(nextBusinessDay);
        } else if (step === 4) {
            // Custom date selected
            finalEffectiveDate = normalizeDate(effectiveDate);
        }

        const payload = {
            ...formData,
            effectiveDate: finalEffectiveDate
        };

        onSave(payload);
        onHide();
    };

    const handleProceedFromWarning = () => {
        if (postWarningAction === 'next') {
            // Save with next business day
            const nextBusinessDay = getNextBusinessDay(new Date());
            const normalizeDate = (date) => {
                if (!date || !(date instanceof Date)) return date;
                if (!remotePriceUpdate) {
                    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
                }
                return date;
            };
            const finalEffectiveDate = normalizeDate(nextBusinessDay);
            const payload = { ...formData, effectiveDate: finalEffectiveDate };
            onSave(payload);
            onHide();
        } else {
            setStep(4);
        }
    };
    const handleOnShow = async () => {
        try {
            const response = await PlanogramService.isAssigned(planogramId);
            setIsNotGenerateTask(response?.assigned === false);
        } catch (error) {
            setIsNotGenerateTask(false);
        }
    }
    const handleCancel = () => {
        setValidationAttempted(false);
        validator.hideMessages();
        setStep(1);
        setEffectiveDate(null);
        setSelectedDevices([]);
        setFormData({ price: null, currency: null });
        onHide();
    };

    // Prepare validation elements
    const shouldShowValidation = validationAttempted;
    const priceErrorEl = shouldShowValidation ? validator.message('change-price-input', t('planogram.cellSettings.price'), formData.price, 'required') : null;

    return (
        <Dialog
            visible={visible}
            onHide={handleCancel}
            header={t('planogram.changePrice.title')}
            className="change-price-dialog"
            modal
            closeOnEscape
            closable
            style={{ width: '520px' }}
            onShow={handleOnShow}
        >
            {step === 1 && (
                <div className="dialog-content">
                    <div className="cells-row">
                        <span className="label">{t('planogram.table.cells')}</span>
                        <div className="assigned-count-badge flex align-items-center gap-2">
                            <span className="assigned-label text-sm">
                                {t('planogram.changedConfiguration.assignedLabel')}
                            </span>
                            <span className="assigned-count">
                                {selectedCellsCount}
                            </span>
                        </div>
                    </div>

                    <div className="price-section">
                        <label htmlFor="change-price-input" className="price-label">
                            {`${t('planogram.cellSettings.price')} (${formData.currency || defaultCurrency || ""})`}
                        </label>
                        <InputAmount
                            id="change-price-input"
                            label={null}
                            value={formData.price}
                            onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                            placeholder="0,00"
                            maxValue={999999}
                            validator={validator}
                            validations={'required'}
                            showValidation={shouldShowValidation}
                        />
                        {priceErrorEl}
                    </div>
                </div>
            )}

            {!isNotGenerateTask && step === 2 && (
                <div className="flex flex-column gap-5 mt-3">
                    <div className="text-color-secondary text-sm line-height-3 m-0 description">
                        {t('planogram.changePrice.step2Description')}
                    </div>

                    <div className="devices-section">
                        <div className="devices-header">
                            <span className="devices-label">{t('planogram.changePrice.devices')}</span>
                            <div className="selected-badge flex align-items-center gap-2">
                                <span className="selected-label text-sm">
                                    {t('planogram.changePrice.selected')}
                                </span>
                                <span className="selected-count">
                                    {selectedDevices.length}
                                </span>
                            </div>
                        </div>

                        <MultiSelect
                            value={selectedDevices}
                            onChange={(e) => setSelectedDevices(e.value)}
                            options={availableDevices}
                            optionLabel="name"
                            placeholder={t('planogram.changePrice.selectDevices')}
                            className="devices-multiselect"
                            loading={isLoadingDevices}
                        />
                    </div>
                </div>
            )}

            {!isNotGenerateTask && step === 3 && (
                <div className="flex flex-column gap-5 mt-3">
                    <div className="warning-text">
                        <WarningTriangleIcon className="warning-icon" />
                        <span>{t('planogram.changePrice.warning.nonRemoteTitle')}</span>
                    </div>
                    <div className="text-color-secondary text-sm line-height-3 m-0 description">
                        {t('planogram.changePrice.warning.nonRemoteDescriptionLine1')}
                        <br />
                        <span dangerouslySetInnerHTML={{ __html: t('planogram.changePrice.warning.nonRemoteDescriptionLine2') }} />
                    </div>

                    <div className="devices-section">
                        <div className="devices-header">
                            <span className="devices-label">{t('planogram.changePrice.devices')}</span>
                            <div className="selected-badge flex align-items-center gap-2">
                                <span className="selected-label text-sm">{t('planogram.changePrice.selected')}</span>
                                <span className="selected-count">{nonRemoteDevices.length}</span>
                            </div>
                        </div>

                        <MultiSelect
                            value={nonRemoteDevices}
                            onChange={handleNonRemoteChange}
                            options={nonRemoteDevices}
                            optionLabel="name"
                            placeholder={t('planogram.changePrice.nonRemoteDevicesPlaceholder')}
                            className="devices-multiselect"
                            showLabel={false}
                        />
                    </div>
                </div>
            )}

            {!isNotGenerateTask && step === 4 && (
                <div className="flex flex-column gap-5 mt-3">
                    <div className="font-inter-subtitile text-primary font-semibold text-base">
                        {planogramName}
                    </div>
                    <div className="text-color-secondary text-sm line-height-3 m-0 description">
                        {t('planogram.copyToDevice.step3Description')}
                    </div>
                    <div className="flex flex-column gap-2 w-full">
                        <label className="font-semibold text-color-secondary text-sm">
                            {t('planogram.copyToDevice.date')}
                        </label>
                        <Calendar
                            value={effectiveDate}
                            onChange={(e) => setEffectiveDate(e.value)}
                            dateFormat="dd/mm/yy"
                            showIcon
                            showTime={remotePriceUpdate}
                            timeOnly={false}
                            placeholder={remotePriceUpdate ? t('planogram.copyToDevice.dateTimePlaceholder') : t('planogram.copyToDevice.datePlaceholder')}
                        />
                    </div>
                </div>
            )}
            {/* buttons section */}
            <div className="flex w-full mt-5">
                {step === 1 && (
                    <Button
                        label={t('planogram.changePrice.save')}
                        onClick={handleSave}
                        disabled={!formData.price}
                        className="p-button-primary w-full"
                    />
                )}
                {!isNotGenerateTask && step === 2 && (
                    <>
                        <Button
                            label={t('planogram.changePrice.nextBusinessDay')}
                            onClick={() => {
                                if (nonRemoteDevices.length > 0) { setPostWarningAction('next'); setStep(3); } else { handleSave(); }
                            }}
                            disabled={selectedDevices.length === 0}
                            className="p-button-primary w-full mr-1"
                        />
                        {remotePriceUpdate ? (
                            <Button
                                label={t('planogram.changePrice.selectedDayAndTime')}
                                onClick={() => { if (nonRemoteDevices.length > 0) { setPostWarningAction('date'); setStep(3); } else { setStep(4); } }}
                                disabled={selectedDevices.length === 0}
                                className="p-button-secondary w-full ml-1"
                            />
                        ) : (
                            <Button
                                label={t('planogram.changePrice.selectedDay')}
                                onClick={() => { if (nonRemoteDevices.length > 0) { setPostWarningAction('date'); setStep(3); } else { setStep(4); } }}
                                disabled={selectedDevices.length === 0}
                                className="p-button-secondary w-full ml-1"
                            />
                        )}
                    </>
                )}
                {!isNotGenerateTask && step === 3 && (
                    <>
                        <Button
                            label={t('common.proceed')}
                            onClick={handleProceedFromWarning}
                            className="p-button-primary w-full mr-1"
                        />
                        <Button
                            label={t('common.cancel')}
                            onClick={handleCancel}
                            className="p-button-secondary w-full ml-1"
                        />
                    </>
                )}
                {!isNotGenerateTask && step === 4 && (
                    <Button
                        label={t('planogram.changePrice.save')}
                        onClick={handleSave}
                        disabled={!effectiveDate}
                        className="p-button-primary w-full"
                    />
                )}
            </div>
        </Dialog>
    );
};

export default ChangePriceDialog;

